/**
 * SettingsTelegramPage — lets the logged-in admin link their Telegram account
 * to receive SchoolBoss push notifications. Multi-tenant safe: each user only
 * receives notifications relevant to their school / branch assignment.
 */
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Send, CheckCircle2, AlertCircle, ExternalLink, Unplug, Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function SettingsTelegramPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [deepLink, setDeepLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/telegram/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll once after the user opens the deep-link so the UI flips to "linked"
  // as soon as they tap START in Telegram.
  useEffect(() => {
    if (!deepLink) return;
    const iv = setInterval(() => { load(); }, 4000);
    return () => clearInterval(iv);
  }, [deepLink, load]);

  useEffect(() => {
    if (status?.linked && deepLink) {
      setDeepLink(null);
      toast.success("Telegram connected!");
    }
  }, [status?.linked, deepLink]);

  const startLink = async () => {
    setLinking(true);
    try {
      const { data } = await api.post("/integrations/telegram/link-token");
      setDeepLink(data);
      window.open(data.deep_link, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLinking(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm("Disconnect your Telegram? You'll stop receiving push alerts.")) return;
    try {
      await api.delete("/integrations/telegram/unlink");
      toast.success("Disconnected");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="settings-telegram-page">
      <header className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#229ED9] to-[#1A7BAB] flex items-center justify-center shadow-floating">
          <Send className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Telegram Notifications</h1>
          <p className="text-sm text-slate-500">
            Push alerts for clearance expirations, school/branch approvals and tuition updates — directly to your Telegram.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="bg-white rounded-2xl border border-brand-purple/15 p-8 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-brand-purple" />
        </div>
      ) : !status?.configured ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            The Telegram bot is not configured on the server. Ask the platform admin to set <code>TELEGRAM_BOT_TOKEN</code> in the backend environment.
          </div>
        </div>
      ) : status?.linked ? (
        <div className="bg-white rounded-2xl border border-brand-purple/15 shadow-floating p-6 space-y-4" data-testid="tg-linked-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#DCFCE7] flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#166534]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-foreground">Telegram is connected</div>
              <div className="text-sm text-slate-500">
                {status.telegram_display_name || status.telegram_username
                  ? `as ${status.telegram_display_name || ""}${status.telegram_username ? ` (@${status.telegram_username})` : ""}`
                  : ""}
              </div>
            </div>
          </div>
          <div className="text-sm bg-brand-purpleLight/40 rounded-xl p-3 text-foreground">
            You'll receive push notifications about your school + branch only. Send <code>/mute</code> in the bot to pause alerts, <code>/unmute</code> to resume.
          </div>
          <Button
            onClick={unlink}
            variant="outline"
            className="rounded-full border-[#C62828]/30 text-[#C62828] hover:bg-[#FEE2E2]"
            data-testid="tg-unlink-btn"
          >
            <Unplug className="w-4 h-4 mr-1.5" /> Disconnect Telegram
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-brand-purple/15 shadow-floating p-6 space-y-4" data-testid="tg-link-card">
          <div className="text-sm text-foreground space-y-2">
            <p>Click <b>Connect Telegram</b>. A new tab opens our bot — tap <b>START</b> there to finish the link.</p>
            <p className="text-slate-500">
              This page will auto-refresh and confirm once you're connected. The link expires in 30 minutes.
            </p>
          </div>
          {deepLink ? (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-900 space-y-2">
              <div>Waiting for you to tap <b>START</b> in Telegram…</div>
              <a
                href={deepLink.deep_link}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#229ED9] font-semibold hover:underline"
                data-testid="tg-reopen-link"
              >
                Re-open the bot <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          ) : null}
          <Button
            onClick={startLink}
            disabled={linking}
            className="rounded-full bg-[#229ED9] hover:bg-[#1A7BAB] text-white h-11 px-6"
            data-testid="tg-connect-btn"
          >
            {linking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Connect Telegram
          </Button>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-6 leading-relaxed">
        Multi-tenant: each linked account only receives notifications about its own school + assigned branches. SchoolBoss never sees your Telegram contacts or message history — we only know your numeric chat-id used to push notifications.
      </p>
    </div>
  );
}
